import {
  useAddCustomerDocument,
  useDeleteCustomerDocument,
  useGetCustomerDocuments,
} from '@/hooks/requests/useCustomers';
import { useParams } from 'react-router-dom';
import { Card, IconButton, Stack, Typography } from '@mui/material';
import {
  SVButton,
  SVFilePicker,
  SVForm,
  SVAutocomplete,
  SVTable,
} from '../core';
import { useForm } from 'react-hook-form';
import {
  AddCustomerDocumentFields,
  CustomerDocumentType,
  CustomerIdentityDocumentType,
  CustomerProofOfAddressDocumentType,
} from '@/types/customers';
import { MESSAGES } from '@/constants/messages';
import showToast from '@/utils/toast';
import { SVTableRow } from '@/types/common';
import { CloudDownload, Delete } from '@mui/icons-material';
import { useDownloadPresignedUrl } from '@/hooks/requests/usePresignedUrls';

export default function CustomerDocuments() {
  const { uuid = '' } = useParams();
  const { customerDocuments, isCustomerDocumentsPending, refetchDocuments } =
    useGetCustomerDocuments(uuid);
  const { addCustomerDocument } = useAddCustomerDocument();
  const { deleteCustomerDocument } = useDeleteCustomerDocument();
  const { downloadPresignedUrl } = useDownloadPresignedUrl();

  const formMethods = useForm<AddCustomerDocumentFields>({
    defaultValues: {
      url: '',
      documentDisplayName: '',
      customerId: uuid,
      side: '',
      documentType: '',
      documentSubtype: '',
    },
    mode: 'onBlur',
  });
  const [url, documentType] = formMethods.watch(['url', 'documentType']);

  if (isCustomerDocumentsPending) {
    return null;
  }

  async function onDeleteCustomerDocument(id: string) {
    try {
      await deleteCustomerDocument(id);
      refetchDocuments();
      showToast({
        type: 'success',
        data: {
          content: 'The document has been succesfully deleted',
        },
      });
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  async function onDownloadDocument(uuid: string, file: string) {
    const url = new URL(file);
    try {
      const response = await downloadPresignedUrl({
        type: 'business document',
        uuid,
        file: url.pathname.slice(1),
      });
      window.open(response.url, '_blank');
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  const rows: SVTableRow[] = customerDocuments.map(document => {
    const { uuid, filename, consumerDocumentType, url } = document;

    return {
      key: `documentRow${uuid}`,
      cells: [
        {
          content: filename,
          type: 'text',
          key: `filename${uuid}`,
        },
        {
          content: consumerDocumentType,
          type: 'text',
          key: `type${uuid}`,
        },
        {
          content: (
            <IconButton onClick={() => onDownloadDocument(uuid, url)}>
              <CloudDownload />
            </IconButton>
          ),
          type: 'custom',
          key: `download${uuid}`,
        },
        {
          content: (
            <IconButton onClick={() => onDeleteCustomerDocument(uuid)}>
              <Delete />
            </IconButton>
          ),
          type: 'custom',
          key: `delete${uuid}`,
        },
      ],
    };
  });

  const customerDocumentTypeOptions = Object.entries(CustomerDocumentType).map(
    ([key, value]) => ({
      label: value,
      value: value,
      key: key,
    }),
  );
  const customerDocumentSubtypeOptions =
    documentType === CustomerDocumentType.IDENTITY_DOCUMENT
      ? Object.entries(CustomerIdentityDocumentType).map(([key, value]) => ({
          label: value,
          value: value,
          key: key,
        }))
      : Object.entries(CustomerProofOfAddressDocumentType).map(
          ([key, value]) => ({
            label: value,
            value: value,
            key: key,
          }),
        );

  async function onAddCustomerDocument(data: AddCustomerDocumentFields) {
    try {
      await addCustomerDocument(data);
      formMethods.reset();
      refetchDocuments();
      showToast({
        type: 'success',
        data: {
          content: 'The document has been succesfully added',
        },
      });
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <>
      <Card>
        <Typography variant="h5" mb={2}>
          Documents
        </Typography>
        {customerDocuments.length === 0 ? (
          <Typography>No documents added</Typography>
        ) : (
          <SVTable
            ariaLabel="Documents"
            headers={[
              {
                content: 'Name',
                key: 'name',
              },
              { content: 'Type', key: 'type' },
              {
                content: 'Download',
                key: 'download',
              },
              {
                content: 'Delete',
                key: 'delete',
              },
            ]}
            rows={rows}
          />
        )}
      </Card>
      <Card>
        <Typography variant="h5" mb={2}>
          Upload Document
        </Typography>
        <SVForm methods={formMethods} onSubmit={onAddCustomerDocument}>
          <Stack spacing={2}>
            <SVAutocomplete
              name="documentType"
              label="Document Type"
              options={customerDocumentTypeOptions}
              rules={{ required: true }}
            />
            {documentType !== '' &&
              documentType !== CustomerDocumentType.OTHER && (
                <SVAutocomplete
                  name="documentSubtype"
                  label={
                    documentType === CustomerDocumentType.IDENTITY_DOCUMENT
                      ? 'Identity Document Type'
                      : 'Proof of Address Type'
                  }
                  options={customerDocumentSubtypeOptions}
                  rules={{ required: true }}
                />
              )}
            <SVAutocomplete
              name="side"
              label="Document Side"
              options={[
                {
                  label: 'Front',
                  value: 'front',
                },
                {
                  label: 'Back',
                  value: 'back',
                },
              ]}
              rules={{ required: true }}
            />
            <SVFilePicker
              name="url"
              documentDisplayName="documentDisplayName"
              label="Select Document"
              presignedUrlFileType="consumer document"
              acceptedFileTypes=".png,.jpg,.jpeg,.pdf"
              uuid={uuid}
              rules={{ required: true }}
            />
            <SVButton disabled={!url} type="submit">
              Upload
            </SVButton>
          </Stack>
        </SVForm>
      </Card>
    </>
  );
}
